// nuolino-admin/src/auth/guards.tsx
// ProtectedRoute e RoleGuard — wrappers que renderizam children
// SOMENTE quando o estado da sessão permitir. UX-only; segurança
// real fica no BFF (RequireNutritionistRole).

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { useAuth } from './useAuth';
import type { UserRole } from './AuthProvider';

export interface ProtectedRouteProps {
  children: ReactNode;
  /** Rota de redirecionamento para login. Default '/login'. */
  loginPath?: string;
}

export function ProtectedRoute({ children, loginPath = '/login' }: ProtectedRouteProps) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg" role="status" aria-live="polite">
        <Spinner size="lg" />
      </div>
    );
  }

  if (state.status === 'unauthenticated' || state.status === 'expired') {
    const reason = state.status === 'expired' ? 'expired' : null;
    const search = reason ? `?reason=${reason}` : '';
    return <Navigate to={`${loginPath}${search}`} replace state={{ from: location.pathname }} />;
  }

  if (state.status === 'forbidden') {
    return <Navigate to="/forbidden" replace state={{ reason: state.reason }} />;
  }

  return <>{children}</>;
}

export interface RoleGuardProps {
  children: ReactNode;
  allow: readonly UserRole[];
  /** Rota alternativa quando role não bate. Default '/forbidden'. */
  fallbackPath?: string;
}

export function RoleGuard({ children, allow, fallbackPath = '/forbidden' }: RoleGuardProps) {
  const { state } = useAuth();
  if (state.status !== 'authenticated') return <>{children}</>;
  if (!allow.includes(state.user.role)) return <Navigate to={fallbackPath} replace />;
  return <>{children}</>;
}
